import React, { useCallback, useMemo } from 'react'
import { CustomerType, GuestFragment, GuestsLookupDocument } from 'src/api/api.generated'
import BaseCustomerLookup from './base/base-customer-lookup'
import { SimpleCustomerLookupOption } from './option/simple-customer-lookup-option'
import { FooterOption } from './option/simple-customer-lookup-footer-option'
import { splitCustomerName } from './utils-lookup'
import { useTranslation } from 'react-i18next'

export type GuestLookupProps = {
  value: GuestFragment | { id: string } | null
  onChange: (value: GuestFragment | null) => void
  onGuestCreateClick: (name: { lastName: string; firstName: string }) => void
  disabled?: boolean
  error?: boolean
  minSearchParameterLenght?: number
  clearOnBlur?: boolean
  onBlur?: (name: { lastName: string; firstName: string }) => void
  size?: 'small' | 'medium'
}

const GuestLookup = React.memo(GuestLookupComponent, (prev, next) => {
  if (prev.onChange !== next.onChange) {
    return false
  }
  if (prev.onGuestCreateClick !== next.onGuestCreateClick) {
    return false
  }
  if (prev.disabled !== next.disabled) {
    return false
  }
  if (prev.error !== next.error) {
    return false
  }
  if (JSON.stringify(prev.value) !== JSON.stringify(next.value)) {
    return false
  }
  return true
})

export default GuestLookup

function GuestLookupComponent({ value, onChange, disabled, error, onGuestCreateClick, minSearchParameterLenght, clearOnBlur, onBlur, size }: GuestLookupProps) {
  const minTextLenght = minSearchParameterLenght || 3
  const { t } = useTranslation()
  
  const onBlurHandler = useCallback(
    (inputValue: string) => {
      if (onBlur && inputValue.length > minTextLenght) {
        onBlur(splitCustomerName(inputValue))
      }
    },
    [onBlur],
  )

  return (
    <BaseCustomerLookup<GuestFragment> //
      customerType={CustomerType.PERSON}
      value={value}
      onChange={onChange}
      queryDocument={GuestsLookupDocument}
      renderOption={(option) => <SimpleCustomerLookupOption option={option} />}
      disabled={disabled}
      error={error}
      minSearchParameterLenght={minTextLenght}
      clearOnBlur={clearOnBlur}
      onBlur={onBlurHandler}
      size={size}
      footerOption={(inputValue, onClose) => (
        <GuestFooterOption //
          text={inputValue}
          onCreatePersonClick={onGuestCreateClick}
          onClose={onClose}
          minSearchParameterLenght={minTextLenght}
          caption={t('pms.components.customer_lookup.name_input_tip')}
        />
      )}
    />
  )
}

type GuestFooterOptionProps = {
  text: string
  onCreatePersonClick: (name: { lastName: string; firstName: string }) => void
  onClose: () => void
  minSearchParameterLenght?: number
  caption:string
}

export function GuestFooterOption(props: GuestFooterOptionProps) {
  const { text, onCreatePersonClick, onClose, caption } = props
  const { t } = useTranslation()

  const minSearchParameterLenght = props.minSearchParameterLenght || 3

  const input = useMemo(() => {
    const nameObj = splitCustomerName(text)
    const valid = text.length > minSearchParameterLenght
    return { ...nameObj, valid }
  }, [text, minSearchParameterLenght])

  return (
    <FooterOption //
      text={text}
      disabled={!input.valid}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => {
        onCreatePersonClick({ lastName: input.lastName, firstName: input.firstName })
        onClose()
      }}
      placeholder={t('pms.components.customer_lookup.create')}
      caption={caption}
      // caption={t('pms.components.customer_lookup.name_input_tip')}
    />
  )
}
